import type Plan from '~/types/Plan';

import { type UserPlan } from '~/types/Plan';

import { ActionKeys } from './constants';

export const fetchUserPlans = () =>
  <const>{
    type: ActionKeys.FETCH_USER_PLANS,
  };

export const fetchUserPlansSuccess = (userPlans: UserPlan[]) =>
  <const>{
    payload: {
      userPlans,
    },
    type: ActionKeys.FETCH_USER_PLANS_SUCCESS,
  };

export const fetchUserPlansError = (errorMessage: string) =>
  <const>{
    payload: {
      errorMessage,
    },
    type: ActionKeys.FETCH_USER_PLANS_ERROR,
  };

export const fetchPricingPlans = () =>
  <const>{
    type: ActionKeys.FETCH_PRICING_PLANS,
  };

export const fetchPricingPlansSuccess = (pricingPlans: Plan[]) =>
  <const>{
    payload: {
      pricingPlans,
    },
    type: ActionKeys.FETCH_PRICING_PLANS_SUCCESS,
  };

export const fetchPricingPlansError = (errorMessage: string) =>
  <const>{
    payload: {
      errorMessage,
    },
    type: ActionKeys.FETCH_PRICING_PLANS_ERROR,
  };

export type PlanUpdateEntryPoints =
  | 'BYOA'
  | 'adjust-plan'
  | 'adjust-plan-downgrade'
  | 'carrier-accounts'
  | 'monthly_label_limit'
  | 'store_connection';

/**
 * Update the user plan
 * @param planName display name of plan to update to
 * @param entryPoint entry point for the plan change, used for tracking
 */
export const updateUserPlan = (planName: string, entryPoint?: PlanUpdateEntryPoints) =>
  <const>{
    payload: { entryPoint: entryPoint ?? undefined, planName },
    type: ActionKeys.UPDATE_USER_PLAN,
  };

export const updateUserPlanSuccess = (userPlan: UserPlan, source?: 'downgrade' | 'upgrade') =>
  <const>{
    payload: {
      source,
      userPlan,
    },
    type: ActionKeys.UPDATE_USER_PLAN_SUCCESS,
  };

export const updateUserPlanError = (errorMessage: string) =>
  <const>{
    payload: {
      errorMessage,
    },
    type: ActionKeys.UPDATE_USER_PLAN_ERROR,
  };

export const resetSetPlanRequest = () =>
  <const>{
    type: ActionKeys.RESET_USER_PLAN_REQUEST,
  };

export const closeWixProPlanNotification = () =>
  <const>{
    type: ActionKeys.CLOSE_WIX_PRO_PLAN_NOTIFICATION,
  };

export const openCloseAccountModal = () =>
  <const>{
    type: ActionKeys.OPEN_CLOSE_ACCOUNT_MODAL,
  };

export const dismissCloseAccountModal = () =>
  <const>{
    type: ActionKeys.DISMISS_CLOSE_ACCOUNT_MODAL,
  };

export type CloseAccountSSOAuth = {
  auth_code: string;
  provider: string;
  state: string;
};

export const submitCloseAccountForm = (
  reason: string,
  tellUsMore: string,
  auth: { password: string } | CloseAccountSSOAuth,
) =>
  <const>{
    payload: {
      auth,
      reason,
      tellUsMore,
    },
    type: ActionKeys.SUBMIT_CLOSE_ACCOUNT_FORM,
  };

export const closeAccountSuccess = (closeStatus: string) =>
  <const>{
    payload: {
      closeStatus,
    },
    type: ActionKeys.CLOSE_ACCOUNT_SUCCESS,
  };

export const closeAccountError = (errorMessage: string) =>
  <const>{
    payload: {
      errorMessage,
    },
    type: ActionKeys.CLOSE_ACCOUNT_ERROR,
  };

const setAdjustedPlan = (plan: Plan) =>
  <const>{
    payload: {
      plan,
    },
    type: ActionKeys.SET_ADJUSTED_PLAN,
  };

export type PlansActions =
  | ReturnType<typeof closeAccountError>
  | ReturnType<typeof closeAccountSuccess>
  | ReturnType<typeof closeWixProPlanNotification>
  | ReturnType<typeof dismissCloseAccountModal>
  | ReturnType<typeof fetchPricingPlans>
  | ReturnType<typeof fetchPricingPlansError>
  | ReturnType<typeof fetchPricingPlansSuccess>
  | ReturnType<typeof fetchUserPlans>
  | ReturnType<typeof fetchUserPlansError>
  | ReturnType<typeof fetchUserPlansSuccess>
  | ReturnType<typeof openCloseAccountModal>
  | ReturnType<typeof resetSetPlanRequest>
  | ReturnType<typeof setAdjustedPlan>
  | ReturnType<typeof submitCloseAccountForm>
  | ReturnType<typeof updateUserPlan>
  | ReturnType<typeof updateUserPlanError>
  | ReturnType<typeof updateUserPlanSuccess>;
